import _ from "lodash";
import { getAcademicYearById } from "modules/Services/ServicesUtils";
/**
 * Helper function to split path into segments and clean them
 */
const _getPathSegments = path => {
  return path.split("/").filter(Boolean);
};

/**
 * Helper function to get route matching details
 * Returns match information for a route against current path segments
 */
const _getRouteMatchDetails = (route, pathSegments, startIndex) => {
  const routeSegments = _getPathSegments(route);
  let matchCount = 0;

  // Count exact matching segments
  for (
    let i = 0;
    i < routeSegments.length && startIndex + i < pathSegments.length;
    i++
  ) {
    if (routeSegments[i] === pathSegments[startIndex + i]) {
      matchCount++;
    } else {
      // Break on first mismatch as we need exact matches
      return {
        isMatch: false,
        matchCount: 0,
        routeLength: routeSegments.length,
      };
    }
  }

  // Route matches only if all segments match
  const isMatch = matchCount === routeSegments.length;

  return {
    isMatch,
    matchCount: isMatch ? matchCount : 0, // Only count matches if all segments match
    routeLength: routeSegments.length,
  };
};

/**
 * Helper to recursively collect leaf nodes (nodes with routes and no children)
 * from a given parent node. Used for excludeRouteFromPath nodes whose leaf
 * children carry the full route for matching.
 */
const _getLeafNodes = (node, nodesByKey) => {
  if (_.isEmpty(node?.children)) {
    return node?.route ? [node] : [];
  }
  return _.flatMap(node.children, childId =>
    _getLeafNodes(nodesByKey[childId], nodesByKey)
  );
};

/**
 * Time complexity: O(n^3) -> N nodes * String comparison * k path segments
 */
export const getSelectedNodeIdBasedOnPath = ({ nodes, path }) => {
  const pathSegments = _getPathSegments(path);
  let selectedNode = null;
  let currentSegmentIndex = 0;

  //We need to start checking from the levelId L1 nodes only.
  let nodesForComparison = _.filter(nodes, ({ levelId }) => levelId === "L1");
  const nodesByKey = _.keyBy(nodes, "id");

  while (currentSegmentIndex < pathSegments.length) {
    // Nodes with excludeRouteFromPath should not be matched directly —
    // instead, expand to their leaf children for matching against the full path.
    const directMatchNodes = _.filter(
      nodesForComparison,
      node => node.route && !node.excludeRouteFromPath
    );
    const excludeRouteNodes = _.filter(
      nodesForComparison,
      node => node.excludeRouteFromPath
    );
    const expandedLeafNodes = _.flatMap(excludeRouteNodes, node =>
      _getLeafNodes(node, nodesByKey)
    );
    const matchingNodes = [...directMatchNodes, ...expandedLeafNodes];

    if (matchingNodes.length > 0) {
      // Get match scores for all nodes
      const nodesWithScores = matchingNodes.map(node => ({
        node,
        ..._getRouteMatchDetails(node.route, pathSegments, currentSegmentIndex),
      }));

      // Only consider exact matches (all segments of route must match)
      const exactMatches = _.filter(nodesWithScores, "isMatch");

      // Among exact matches, prefer longer routes (more specific paths).
      // When multiple candidates tie on routeLength, use lookahead to pick
      // the one whose children can match the next path segment.
      const sortedMatches = _.orderBy(exactMatches, "routeLength", "desc");
      const topRouteLength = sortedMatches[0]?.routeLength;
      const tiedMatches = _.filter(
        sortedMatches,
        m => m.routeLength === topRouteLength
      );

      if (tiedMatches.length > 1) {
        const nextSegmentIndex = currentSegmentIndex + (topRouteLength || 0);

        selectedNode =
          _.find(tiedMatches, ({ node }) => {
            let childNodes = _.map(
              node?.children,
              childId => nodesByKey[childId]
            );
            childNodes = _.flatMap(childNodes, n => {
              if (_.isEmpty(n?.route)) {
                return _.map(_.get(n, "children", []), id => nodesByKey[id]);
              }
              return n;
            });
            return _.some(
              childNodes,
              child =>
                child?.route &&
                _getRouteMatchDetails(
                  child.route,
                  pathSegments,
                  nextSegmentIndex
                ).isMatch
            );
          })?.node || tiedMatches[0]?.node;
      } else {
        selectedNode = sortedMatches[0]?.node;
      }

      if (selectedNode) {
        // Update segment index based on matched segments
        const matchedSegments = _getPathSegments(selectedNode.route);
        currentSegmentIndex += matchedSegments.length;

        // Get next level nodes for comparison
        nodesForComparison = _.map(
          selectedNode?.children,
          childId => nodesByKey[childId]
        );

        // Handle L2 nodes variants (leaf nodes with route and non-leaf nodes with children)
        nodesForComparison = _.flatMap(nodesForComparison, node => {
          if (_.isEmpty(node?.route)) {
            const children = _.get(node, "children", []);
            return _.map(children, childId => nodesByKey[childId]);
          }
          return node;
        });
      } else {
        break;
      }
    } else {
      break;
    }
  }

  return selectedNode?.id;
};

export const getSidebarMenuTreeStructureV2 = ({ parent, menuId, nodes }) => {
  let currentNodes = [];
  let maxDepth = 0;

  const item = _.find(nodes, { id: menuId });

  const parentNode = _.find(nodes, { id: parent });

  const {
    id: key,
    children,
    prefix,
    suffix,
    isSliderSubmenu,
    listType,
    isExpanded,
    label,
    route,
    levelId,
  } = item;

  _.forEach(children, childId => {
    const { depth, nodes: childrenNodes } = getSidebarMenuTreeStructureV2({
      menuId: childId,
      nodes,
      parent: key,
    });

    maxDepth = _.max([depth, maxDepth]);
    currentNodes = [...currentNodes, ...childrenNodes];
  });

  const isDivider = _.get(item, "isDivider", false);

  const node =
    key === "root"
      ? {
          key: "root",
          children,
        }
      : {
          key,
          title: label,
          children,
          isSliderSubmenu,
          tier:
            listType === "accordion" ? 2 : isSliderSubmenu ? 1 : maxDepth + 1,
          parent,
          sliderSubmenu: {
            subtext: parentNode?.label,
            prefix: null,
          },
          ...(prefix ? { prefix } : {}),
          ...(suffix ? { suffix } : {}),
          ...(isDivider ? { isDivider } : {}),
          ...(listType ? { listType } : {}),
          ...(isExpanded !== undefined ? { isExpanded } : {}),
          route,
          levelId,
        };

  // Only adjust maxDepth for accordion nodes — their fixed tier (2)
  // needs to be accounted for in the parent's depth calculation.
  if (listType === "accordion") {
    maxDepth = Math.max(maxDepth, 1 + node.tier);
  }

  currentNodes.push(node);

  return {
    nodes: currentNodes,
    depth: isSliderSubmenu ? 1 : maxDepth + 1,
  };
};

export const modulesUsingDsVersion1 = [
  "ATTENDANCE_SETTINGS",
  "SCHOOL_CALENDAR_V2",
  "USER_NAME_SETTINGS_V2",
  "TRANSCRIPTS",
  "TRANSCRIPTS_SETUP",
  "TRANSCRIPT_YEAR_GROUPS",
  "TRANSCRIPT_CONFIGURE_TEMPLATE",
  "UNIT_PLAN_PDF_DOWNLOADER",
  "SCHOOL_ANALYTICS",
  "ASSESSMENT_TRANSFER",
  "ACTIVITY_LOGS",
  "BULK_UPLOAD_ACTIONS",
  "SYNC",
  "SYNC_INTEGRATION",
  "SYNCV2",
  "SYNCV2_INTEGRATION",
  "ACTIVITY_SETTINGS",
  "REVIEWER_SETUP",
  "ANNOUNCEMENT_REVIEWER_SETUP",
  "LTI_SETTINGS",
  "OPEN_API_SETUP",
  "INTEGRATION_ANALYTICS",
  "SUBJECTS",
  "SUBJECT_GROUPS_AND_SUBJECTS",
  "SCOPE_SEQUENCE",
  "GRADING_PERIODS",
  "ATTENDANCE_RECORDING",
  "ATTENDANCE_CALCULATION",
  "GRADING_METHODOLOGY",
  "TIMETABLE_MANAGER",
  "DP_IBIS_EXAM_REGISTRATION",
  "DP_IB_E_COURSEWORK",
  "ADMIN_GRADEBOOK",
  "UBD_PLANNING_INSIGHTS",
  "DP_PLANNING_INSIGHTS",
  "PYP_PLANNING_INSIGHTS",
  "MYP_PLANNING_INSIGHTS",
  "LEARNING_STANDARDS",
  "DP_SUBJECT_STANDARD",
  "TIMETABLE_SYNC",
  "ATTENDANCE_SYNC",
  "GRADEBOOK_SYNC",
  "ASSESSMENT_SYNC",
  "ROLES_AND_PERMISSIONS",
  "INTEGRATIONS_INTERNAL_TOOL",
  "SETUP_GUIDE",
  "ATTENDANCE_CATEGORIES_V2",
  "ATTENDANCE_RECORDING",
  "ATTENDANCE_CALCULATION",
  "PERMISSIONS",
  "NOTIFICATION_MANAGER",
  "DP_IB_E_COURSEWORK",
  "TRANSCRIPTS",
  "TIMETABLE_CHECKLIST",
  "PERIODS_V1",
  "BELL_SCHEDULES_V1",
  "PERIODS",
  "BELL_SCHEDULES",
  "ROUTINES",
  "SCHEDULE_CALENDAR",
  "CLASS_TIMETABLE",
  "STUDENT_TIMETABLE",
  "TEACHER_TIMETABLE",
  "IMPORT_LOG",
  "UBD_LEARNING_STANDARD",
];

export const modulesWithFullScreenModals = [
  "DP_IBIS_EXAM_REGISTRATION",
  "DP_IBIS_REGISTRATION",
  "SCHOOL_ANALYTICS",
  "ADMIN_GRADEBOOK",
  "UBD_PLANNING_INSIGHTS",
  "DP_PLANNING_INSIGHTS",
  "PYP_PLANNING_INSIGHTS",
  "MYP_PLANNING_INSIGHTS",
  "TRANSCRIPTS_V3",
  "CALENDAR_SETTINGS",
];

export const getInitialNode = nodes => {
  const rootNode = _.find(nodes, { id: "root" });

  const firstChildNodeId = _.head(_.get(rootNode, "children", []));

  const childNode = _.find(nodes, { id: firstChildNodeId });

  if (!_.isEmpty(_.get(childNode, "children", []))) {
    const firstGrandChildNodeId = _.head(_.get(childNode, "children", []));
    return _.find(nodes, { id: firstGrandChildNodeId });
  }

  return childNode;
};

export const getInitialRoute = nodes => {
  return getInitialNode(nodes)?.route;
};

const _getVisiblePortionsOfNode = ({ node, nodesByKeys, params }) => {
  const { isVisible, children, tabChildren } = node;

  let isNodeVisible = true;

  isNodeVisible = isVisible ? isVisible({ params, node }) : true;

  if (isNodeVisible) {
    if (_.size(children) > 0) {
      const newNode = { ...node };

      let visibleChildren = _.reduce(
        children,
        (result, childId) => {
          const visibleChildNode = _getVisiblePortionsOfNode({
            node: nodesByKeys[childId],
            nodesByKeys,
            params,
          });

          if (visibleChildNode) {
            result.push(childId);
          }
          return result;
        },
        []
      );

      /**
       * When only divider nodes are visible, then we should discard them as
       * entire node should not be visible.
       */
      const areAllVisibleChildrenDividerNodes = _.every(
        visibleChildren,
        childId => nodesByKeys[childId]?.isDivider
      );

      if (areAllVisibleChildrenDividerNodes) {
        visibleChildren = [];
      }

      if (_.size(visibleChildren) > 0) {
        newNode.children = visibleChildren;
        return newNode;
      }

      return null;
    }

    if (_.size(tabChildren) > 0) {
      const newNode = { ...node };

      const visibleTabChildren = _.reduce(
        tabChildren,
        (result, childId) => {
          const childNode = nodesByKeys[childId];
          if (!childNode) return result;

          const isChildVisible = childNode.isVisible
            ? childNode.isVisible({ params, node: childNode })
            : true;

          if (isChildVisible) {
            result.push(childId);
          }
          return result;
        },
        []
      );

      if (_.size(visibleTabChildren) > 0) {
        newNode.tabChildren = visibleTabChildren;
        return newNode;
      }

      return null;
    }

    return node;
  }

  return null;
};

const _getDynamicNodes = ({ nodes, params }) => {
  const { t } = params;

  const updatedNodes = [];
  _.forEach(nodes, node => {
    if (node.getDynamicNode) {
      const { getDynamicNode } = node;
      const dynamicNodes = getDynamicNode({
        params,
        node,
      });

      updatedNodes.push(...dynamicNodes);
    } else {
      const updatedNode = { ...node };
      const localizedLabel = t(node.label);
      updatedNode.label = localizedLabel;

      updatedNodes.push(updatedNode);
    }
  });

  return updatedNodes;
};

/**
 * Timecomplexity: O(n^2) -> In worst case, each node is visited K times. Since, there are N nodes, total processing
 * is O(n*k)
 */
const _getFilteredAdminSettingsNodes = ({ nodes, params }) => {
  const filteredNodes = [];

  const nodesByKeys = _.keyBy(nodes, "id");

  _.forEach(nodes, node => {
    const visibleNode = _getVisiblePortionsOfNode({
      node,
      nodesByKeys,
      params,
    });
    if (visibleNode) {
      filteredNodes.push(visibleNode);
    }
  });

  return filteredNodes;
};

export const getAdminNavigationNodes = ({ params, originalNodes }) => {
  const nodes = originalNodes;

  const dynamicNodes = _getDynamicNodes({ nodes, params });

  return _getFilteredAdminSettingsNodes({ nodes: dynamicNodes, params });
};

/**
 * This util is based on the fact that new ids of the nodes
 * are constructed by joining dynamic parameter and original nodeId with a colon.
 *
 * Note: childId represents the newly generated id of the node.
 */
export const getAreChildIdAndNodeIdSame = ({ childId, nodeId }) => {
  return nodeId === childId || _.split(childId, ":")?.[1] === nodeId;
};

export const getCucrriculumIdForAccountAdminPortal = ({
  nodeId,
  nodes,
  children = ["root"],
}) => {
  let curriculumProgramId = null;

  _.forEach(children, childId => {
    const childNode = _.find(nodes, { id: childId });

    if (!childNode) {
      // If no matching node is found for this childId, skip to the next one
      return;
    }
    if (
      getAreChildIdAndNodeIdSame({ childId: childNode.id, nodeId }) &&
      childNode.curriculumProgramId
    ) {
      curriculumProgramId = childNode.curriculumProgramId;
      return false;
    }

    if (!_.isEmpty(childNode.children)) {
      const childCurriculumProgramId = getCucrriculumIdForAccountAdminPortal({
        nodeId,
        nodes,
        children: childNode.children,
      });

      if (!_.isEmpty(childCurriculumProgramId)) {
        curriculumProgramId = childCurriculumProgramId;
        return false;
      }
    }
  });

  return curriculumProgramId;
};

/**
 * Checks if the current route is accessible based on the computed navigation nodes.
 *
 * - If the root node is missing, no routes are accessible.
 * - If navigation came from the sidebar (childId exists), access is valid.
 * - If accessed directly via URL, the path must match a node in the tree.
 *
 */
export const getIsRouteAccessible = ({
  pathname,
  baseSegment,
  nodes,
  childId,
}) => {
  // Skip access check until navigation nodes are loaded
  if (_.size(nodes) === 0) return true;

  // If root node missing, nothing is accessible
  const rootExists = _.some(nodes, { id: "root" });
  if (!rootExists) return false;

  const path = _.split(pathname, baseSegment)[1] || "";
  const normalizedPath = path.replace(/^\/+/, "");

  // Check if the route is accessed directly (e.g., via URL) and the path is not empty
  const isRouteAccessedDirectly = !childId && normalizedPath?.length > 0;

  if (!isRouteAccessedDirectly) return true;

  // Find the node that corresponds to the current route if route accessed directly
  const currentNode = getSelectedNodeIdBasedOnPath({
    nodes,
    path: normalizedPath,
  });

  return !!currentNode;
};

export const getEffectiveIsCurrentAcademicYearSelected = ({
  isCurrentAcademicYearSelected,
  queryAcademicYearId,
  organizationId,
}) => {
  if (!queryAcademicYearId) return isCurrentAcademicYearSelected;
  const academicYearFromQuery = getAcademicYearById({
    organizationId,
    academicYearId: queryAcademicYearId,
  });
  return academicYearFromQuery
    ? _.get(academicYearFromQuery, "isCurrentAcademicYear", false)
    : isCurrentAcademicYearSelected;
};
